import { getTemplateNavHeaderStyles } from './NavHeader.styles'
import { tryLoadDependencies, WebAwesomeComponent } from '../Global/loadDependency'

const avatarTemplate = document.createElement('template')
avatarTemplate.innerHTML = `
<wa-avatar slot="start" label="Avatar" class="utility-avatar"></wa-avatar>
`
const t = document.createElement('template')
t.innerHTML = `
  <wa-button
    id="utility-profile-btn"
    size="small"
    appearance="plain"
    variant="neutral"
    aria-haspopup="menu"
    aria-expanded="false"
    aria-controls="utility-dropdown-menu"
    aria-label="User profile menu"
    class="profile-btn"
    with-caret
  > 
    <slot name="userDetails"></slot>
  </wa-button>

  <wa-tooltip for="utility-profile-btn" placement="bottom" class="profile-tooltip">
  <div class="tooltip-wrapper">  
    <slot name="tooltip"></slot>
  </div>
  </wa-tooltip>

  <div id="utility-dropdown-menu" class="profile-dropdown-panel closed" role="menu">
    <slot></slot>
  </div>
`

class NavHeaderUtilityMenu extends HTMLElement {
  private handleOutsideClick: (event: MouseEvent) => void
  private handleHostKeydown: (event: KeyboardEvent) => void
  private handleTriggerClick: () => void
  private handleTriggerKeydown: (event: KeyboardEvent) => void

  private static readonly MENU_SELECTOR = '#utility-dropdown-menu'
  private static readonly TRIGGER_SELECTOR = 'wa-button'
  private static readonly FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[role="menuitem"]',
  ].join(',')

  constructor() {
    super()
    tryLoadDependencies(document, [
      WebAwesomeComponent.Button,
      WebAwesomeComponent.Avatar,
      WebAwesomeComponent.Tooltip,
    ])

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.appendChild(getTemplateNavHeaderStyles().content.cloneNode(true))
    shadow.appendChild(t.content.cloneNode(true))

    // Bind handlers once for add/remove symmetry
    this.handleOutsideClick = this._handleOutsideClick.bind(this)
    this.handleHostKeydown = this._handleHostKeydown.bind(this)
    this.handleTriggerClick = this._handleTriggerClick.bind(this)
    this.handleTriggerKeydown = this._handleTriggerKeydown.bind(this)
  }

  connectedCallback() {
    this.addEventListener('keydown', this.handleHostKeydown as EventListener)

    const btn = this.getTriggerButton()
    if (btn) {
      btn.addEventListener('click', this.handleTriggerClick)
      btn.addEventListener('keydown', this.handleTriggerKeydown as EventListener)
    }
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.handleHostKeydown as EventListener)

    const btn = this.getTriggerButton()
    if (btn) {
      btn.removeEventListener('click', this.handleTriggerClick)
      btn.removeEventListener('keydown', this.handleTriggerKeydown as EventListener)
    }

    document.removeEventListener('mousedown', this.handleOutsideClick)
  }

  static get observedAttributes() {
    return ['userprofileinitials', 'is-open']
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'userprofileinitials') {
      this.updateAvatarInitials(newValue)
    } else if (name === 'is-open') {
      if (newValue === 'true') {
        this.openMenu()
      } else if (newValue === 'false') {
        this.closeMenu(false)
      }
    }
  }

  private getTriggerButton(): HTMLElement | null {
    return (this.shadowRoot?.querySelector(NavHeaderUtilityMenu.TRIGGER_SELECTOR) as HTMLElement) || null
  }

  private getMenuPanel(): HTMLElement | null {
    return (this.shadowRoot?.querySelector(NavHeaderUtilityMenu.MENU_SELECTOR) as HTMLElement) || null
  }

  private isDisabled(): boolean {
    return this.getAttribute('disabled') === 'true'
  }

  private isOpen(): boolean {
    return this.getAttribute('is-open') === 'true'
  }

  private setAvatarActiveState(isActive: boolean) {
    const avatar = this.getTriggerButton()?.querySelector<HTMLElement>('wa-avatar')
    avatar?.classList.toggle('active', isActive)
  }

  private createAvatar(parent: HTMLElement): HTMLElement | null {
    const fragment = avatarTemplate.content.cloneNode(true) as DocumentFragment
    const avatar = fragment.querySelector<HTMLElement>('wa-avatar')

    if (avatar) {
      parent.prepend(fragment)
    }

    return avatar
  }

  private updateAvatarInitials(newValue: string) {
    const btn = this.getTriggerButton()
    if (!btn) return

    const avatar = btn.querySelector<HTMLElement>('wa-avatar') ?? this.createAvatar(btn)
    avatar?.setAttribute('initials', newValue || ' ')
  }

  private _handleOutsideClick(event: MouseEvent) {
    const path = event.composedPath()
    if (!path.includes(this)) {
      this.closeMenu()
    }
  }

  private _handleTriggerClick() {
    this.toggleMenu()
  }

  private _handleTriggerKeydown(event: KeyboardEvent) {
    if (this.isDisabled()) {
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!this.isOpen()) {
        this.openMenu()
      }
      this.focusFirstMenuItem()
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!this.isOpen()) {
        this.openMenu()
      }
      this.focusLastMenuItem()
      return
    }

    if (event.key === 'Tab' && this.isOpen() && !event.shiftKey) {
      event.preventDefault()
      this.focusFirstMenuItem()
    }
  }

  private _handleHostKeydown(event: KeyboardEvent) {
    if (!this.isOpen()) return

    const focusableItems = this.getFocusableMenuItems()
    if (!focusableItems.length) return

    if (event.key === 'Escape') {
      event.preventDefault()
      this.closeMenu(true)
      return
    }

    if (event.key !== 'Tab' && event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

    const target = event.target as HTMLElement | null
    const currentIndex = target ? focusableItems.findIndex((item) => item === target) : -1

    // Trap only when focus is inside menu items.
    if (currentIndex < 0) return

    event.preventDefault()
    const direction = event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey) ? -1 : 1
    this.moveFocus(focusableItems, currentIndex, direction)
  }

  private moveFocus(items: HTMLElement[], currentIndex: number, direction: 1 | -1) {
    const nextIndex = (currentIndex + direction + items.length) % items.length
    items[nextIndex]?.focus()
  }

  private getFocusableMenuItems(): HTMLElement[] {
    return Array.from(this.querySelectorAll<HTMLElement>(NavHeaderUtilityMenu.FOCUSABLE_SELECTOR)).filter((element) => {
      const disabled = element.getAttribute('aria-disabled') === 'true'
      const hidden = element.hasAttribute('hidden')
      return !disabled && !hidden
    })
  }

  private focusFirstMenuItem() {
    const focusableItems = this.getFocusableMenuItems()
    focusableItems[0]?.focus()
  }

  private focusLastMenuItem() {
    const focusableItems = this.getFocusableMenuItems()
    focusableItems[focusableItems.length - 1]?.focus()
  }

  private toggleMenu() {
    if (this.isDisabled()) return

    const panel = this.getMenuPanel()
    if (panel?.classList.contains('closed')) {
      this.openMenu()
    } else {
      this.closeMenu()
    }
  }

  private openMenu() {
    const panel = this.getMenuPanel()
    const btn = this.getTriggerButton()

    if (panel && btn && panel.classList.contains('closed')) {
      panel.classList.remove('closed')
      btn.setAttribute('aria-expanded', 'true')
      document.addEventListener('mousedown', this.handleOutsideClick)
      this.setAvatarActiveState(true)
      this.setAttribute('is-open', 'true')
    }
  }

  private closeMenu(returnFocus: boolean = false) {
    const panel = this.getMenuPanel()
    const btn = this.getTriggerButton()

    if (panel && btn && !panel.classList.contains('closed')) {
      panel.classList.add('closed')
      btn.setAttribute('aria-expanded', 'false')
      document.removeEventListener('mousedown', this.handleOutsideClick)
      this.setAvatarActiveState(false)
      this.setAttribute('is-open', 'false')

      if (returnFocus) {
        btn.focus()
      }
    }
  }
}

if (!customElements.get('ts-navheader-utility-menu')) {
  customElements.define('ts-navheader-utility-menu', NavHeaderUtilityMenu)
}
export { NavHeaderUtilityMenu }
